import { faClipboardCheck, faMailBulk, faPrint } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { WizardStepperFooter } from "app/modules/shared/wizard/WizardStepFooter";
import { useSelector } from "react-redux";
import { ServiceChargeInvoicingSteps } from "../../ServiceChargeInvoiceWizardSteps";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  addInvoiceAttachments,
  emailInvoices,
  printInvoices,
  selectEmailProgressResponse,
  selectIsEmailing,
  selectIsPrinting,
  selectIsSavingInvoices,
  selectPrintProgressResponse,
} from "./InvoiceSendingSlice";
import { WizardStepTitle } from "app/modules/shared/wizard/WizardStepTitle";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  completeServiceChargeInvoicingWizard,
  getServiceChargeInvoiceProcessSelectionModel,
  selectServiceChargeInvoice,
} from "../../ServiceChargeInvoicingSlice";
import JSConfetti from "js-confetti";
import { useNavigate } from "react-router-dom";
import { ProgressBar } from "react-bootstrap";
import translations from "utils/translationUtility";
import { YesNoAlert } from "cpl-react-common/components/Alerts/YesNo";
import { Style } from "cpl-react-common/Helpers/AlertHelpers";
import { CPLFileUpload } from "app/modules/shared/cplFileUpload/CPLFileUpload";
import { ServiceChargeInvoiceAttachmentsModel } from "services/classes/ServiceChargeInvoiceAttachmentsModel";
import { FileParameter } from "services/api/cplauthapi/CPLAuthAPI";

const InvoiceSending: React.FC = () => {
  const thunkDispatch = useThunkDispatch();
  const navigate = useNavigate();
  const [sendingEmails, setSendingEmails] = useState(false);
  const [printingInvoices, setPrintingInvoices] = useState(false);
  const [emailsSent, setEmailsSent] = useState(false);
  const [invoicesPrinted, setInvoicesPrinted] = useState(false);
  const emailProgressResponse = useSelector(selectEmailProgressResponse);
  const printProgressResponse = useSelector(selectPrintProgressResponse);
  const serviceChargeInvoice = useSelector(selectServiceChargeInvoice);
  const savingInvoices = useSelector(selectIsSavingInvoices);
  const isEmailing = useSelector(selectIsEmailing);
  const isPrinting = useSelector(selectIsPrinting);
  const windowRef = useRef<Window | null>(null);
  const lastWindowURL = useRef<string | null>(null);
  const [anyEmailInvoices, setAnyEmailInvoices] = useState(false);
  const [anyPrintInvoices, setAnyPrintInvoices] = useState(false);
  const [showPrintAlert, setShowPrintAlert] = useState(false);
  const [showEmailAlert, setShowEmailAlert] = useState(false);
  const nextActionRef = useRef<null | (() => Promise<void>)>(null);
  const [showAttachmentsUploader, setShowAttachmentsUploader] = useState(false);
  const [newAttachments, setNewAttachments] = useState<File[]>([]);

  useEffect(() => {
    const anyEmails = serviceChargeInvoice?.invoiceReportModels?.some((x) => x.nonReportData?.emailInvoice) ?? false;
    const anyPrints = serviceChargeInvoice?.invoiceReportModels?.some((x) => x.nonReportData?.printInvoice) ?? false;

    setAnyEmailInvoices(anyEmails);
    setAnyPrintInvoices(anyPrints);
  }, [serviceChargeInvoice]);

  useEffect(() => {
    if (!savingInvoices && serviceChargeInvoice.batchNumber == undefined)
      thunkDispatch(getServiceChargeInvoiceProcessSelectionModel());
  }, [savingInvoices, serviceChargeInvoice, thunkDispatch]);

  useEffect(() => {
    if (printProgressResponse.printedExternally || printProgressResponse.previewURL == null) {
      if (windowRef.current != null) {
        windowRef.current.close();
        windowRef.current = null;
      }
      return;
    }

    if (printProgressResponse.previewURL === lastWindowURL.current) {
      return;
    }

    lastWindowURL.current = printProgressResponse.previewURL;

    const newWindow = windowRef.current;

    if (!newWindow) {
      return;
    }

    try {
      newWindow.location.href = printProgressResponse.previewURL;
      newWindow.focus();
    } catch (err) {
      newWindow.close();
    }
  }, [printProgressResponse]);

  const onAlertAnswer = async (yes: boolean) => {
    setShowPrintAlert(false);
    setShowEmailAlert(false);

    if (!yes) {
      nextActionRef.current = null;
      return;
    }

    const callback = nextActionRef.current;
    nextActionRef.current = null;
    if (callback) {
      await callback();
    }
  };

  async function completeProcess() {
    const confetti = new JSConfetti();
    await confetti.addConfetti();
    thunkDispatch(completeServiceChargeInvoicingWizard());
    navigate("/dashboard");
  }

  async function printInvoicesClick() {
    await addAttachments();
    if (invoicesPrinted) {
      nextActionRef.current = doPrintInvoices;
      setShowPrintAlert(true);
      return;
    }
    await doPrintInvoices();
  }

  async function doPrintInvoices() {
    setPrintingInvoices(true);

    try {
      const response = await thunkDispatch(printInvoices()).unwrap();
      // Make sure previewurl exists

      if (!response.printedExternally) {
        const newWindow = window.open(response.previewURL, "_blank");
        if (newWindow == null) {
          throw new Error("Unable to open new window");
        }

        newWindow.focus();
      }
    } catch {
      window.close();
    } finally {
      setInvoicesPrinted(true);
    }
  }

  const attachmentsFormData: ServiceChargeInvoiceAttachmentsModel | null = useMemo(() => {
    if (newAttachments.length == 0) return null;
    const attachmentFiles: FileParameter[] = newAttachments.map((file) => {
      return { data: file, fileName: file.name };
    });
    return { attachments: attachmentFiles, batchID: serviceChargeInvoice.batchNumber };
  }, [newAttachments, serviceChargeInvoice]);

  async function addAttachments() {
    if (attachmentsFormData != null) await thunkDispatch(addInvoiceAttachments(attachmentsFormData));
  }

  async function emailInvoicesClick() {
    await addAttachments();
    if (emailsSent) {
      nextActionRef.current = sendInvoicesToEmail;
      setShowEmailAlert(true);
      return;
    }
    await sendInvoicesToEmail();
  }

  async function sendInvoicesToEmail() {
    setSendingEmails(true);
    await thunkDispatch(emailInvoices());
    setEmailsSent(true);
  }

  return (
    <>
      <WizardStepTitle
        stepNumber={ServiceChargeInvoicingSteps.SendInvoices}
        title={"Send and Print Invoices"}
        subtitle={
          "Invoices reports have been generated. Please email and print invoices using the buttons on the screen."
        }
      ></WizardStepTitle>
      <div className="d-flex justify-content-center">
        <div className="d-flex flex-column align-items-center me-2">
          {savingInvoices ? (
            <p>Your invoices are currently being saved!</p>
          ) : (
            <>
              <p>The Service Charge Invoice wizard is complete.</p>
              <p>
                You may now select to email invoices to {translations.client.toLocaleLowerCase()}s that wish to receive
                invoices via email
              </p>
              <p>
                or print invoices for {translations.client.toLocaleLowerCase()}s that wish to receive invoices via post
              </p>
            </>
          )}
        </div>
      </div>
      <div>
        {sendingEmails && (
          <>
            <ProgressBar
              min={0}
              now={emailProgressResponse.current}
              max={emailProgressResponse.total}
              variant="success"
              striped
              animated
            />
            <div className="d-flex justify-content-center">
              <div className="d-flex flex-column align-items-center me-2">{emailProgressResponse.message}</div>
            </div>
          </>
        )}
      </div>
      <br />
      <div>
        {printingInvoices && (
          <>
            <div className="d-flex justify-content-center">
              <div className="d-flex flex-column align-items-center me-2">{printProgressResponse.message}</div>
            </div>
          </>
        )}
      </div>
      <WizardStepperFooter>
        <div hidden={!showAttachmentsUploader} className="mb-4">
          <CPLFileUpload
            data-test-id="send-invoices-step__attachments-file-upload"
            updateNewFiles={(files) => {
              setNewAttachments(files);
            }}
          ></CPLFileUpload>
        </div>
        <div className="d-flex justify-content-between">
          <div>
            <button
              data-test-id="send-invoices-step__add-attachments-button"
              className="btn btn-secondary btn-sm"
              type="button"
              onClick={() => setShowAttachmentsUploader(!showAttachmentsUploader)}
            >
              Add Attachments
            </button>
          </div>
          <div>
            <button
              data-test-id="send-invoices-step__email-invoices-button"
              className="btn btn-success btn-sm"
              type="button"
              onClick={() => emailInvoicesClick()}
              disabled={savingInvoices || isEmailing || !anyEmailInvoices}
            >
              <FontAwesomeIcon icon={faMailBulk} />
              <span className="ms-1">Email Invoices</span>
            </button>
          </div>
          <div>
            <button
              data-test-id="send-invoices-step__print-invoices-button"
              className="btn btn-success btn-sm"
              type="button"
              onClick={() => printInvoicesClick()}
              disabled={savingInvoices || isPrinting || !anyPrintInvoices}
            >
              <FontAwesomeIcon icon={faPrint} />
              <span className="ms-1">Print Invoices</span>
            </button>
          </div>
          <div>
            <button
              data-test-id="send-invoices-step__complete-button"
              className="btn btn-primary btn-sm"
              type="button"
              onClick={() => completeProcess()}
              disabled={savingInvoices}
            >
              <FontAwesomeIcon icon={faClipboardCheck} />
              <span className="ms-1">Complete</span>
            </button>
          </div>
        </div>
      </WizardStepperFooter>
      <YesNoAlert
        showAlert={showPrintAlert}
        alertTitle="Print invoices"
        alertText="You've already printed invoices. Print again?"
        callBack={onAlertAnswer}
        style={Style.Warning}
      />
      <YesNoAlert
        showAlert={showEmailAlert}
        alertTitle="Resend emails"
        alertText="You've already sent emails. Resend them?"
        callBack={onAlertAnswer}
        style={Style.Warning}
      />
    </>
  );
};

export default InvoiceSending;
